import { cn } from '@heidi/utils';
import * as m from 'motion/react-m';
import { type ComponentPropsWithoutRef } from 'react';
import { bubbleEnterExitAnimation } from '../animation';
import {
  BUBBLE_BASE_CLASSES,
  BUBBLE_OPAQUE_CLASSES,
  BUBBLE_TRANSLUCENT_CLASSES,
} from '../styles';

type MotionDivProps = ComponentPropsWithoutRef<typeof m.div>;

type AnimationProps = Pick<MotionDivProps, 'initial' | 'animate' | 'exit' | 'transition'>;

interface DictationBubbleShellProps {
  children: React.ReactNode;
  className?: string;
  animation?: AnimationProps;
  /** Render with a fully opaque surface (used for error states — DEV-11867). */
  opaque?: boolean;
  'data-testid'?: string;
  onMouseEnter?: React.MouseEventHandler<HTMLElement>;
  onMouseMove?: React.MouseEventHandler<HTMLElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLElement>;
  /**
   * When provided, the bubble renders as a real, focusable `<button>` instead
   * of a decorative `<div>` — keeping a single shared shell while avoiding the
   * onClick-on-`<div>` anti-pattern. The native button handles Enter/Space
   * activation and focus natively, so no `role`/`tabIndex`/`onKeyDown` plumbing
   * is needed. Omit it for the non-interactive states (listening, processing,
   * errors) and the shell stays a plain animated `<div>`.
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  'aria-label'?: string;
}

export const DictationBubbleShell = ({
  children,
  className,
  animation = bubbleEnterExitAnimation,
  opaque = false,
  'data-testid': testId,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
  onClick,
  'aria-label': ariaLabel,
}: DictationBubbleShellProps) => {
  const surfaceClassName = cn(
    BUBBLE_BASE_CLASSES,
    opaque ? BUBBLE_OPAQUE_CLASSES : BUBBLE_TRANSLUCENT_CLASSES,
    className
  );

  const sharedProps = {
    'data-testid': testId,
    onMouseEnter,
    onMouseMove,
    onMouseLeave,
    'aria-label': ariaLabel,
    ...animation,
  };

  if (onClick) {
    return (
      <m.button
        type="button"
        // `appearance-none text-start` strip the native control look so the
        // interactive bubble is visually identical to the decorative `<div>`.
        className={cn('appearance-none text-start', surfaceClassName)}
        onClick={onClick}
        {...sharedProps}
      >
        {children}
      </m.button>
    );
  }

  return (
    <m.div className={surfaceClassName} {...sharedProps}>
      {children}
    </m.div>
  );
};
